import { env } from '@/lib';

export interface ApiError {
  status: number;
  statusText: string;
  message: string;
  data?: unknown;
}

export interface RequestOptions extends Omit<RequestInit, 'method' | 'body'> {
  params?: Record<string, string | number | boolean | undefined>;
  // Services on their own origin (e.g. notifyd) override the default base; auth stays the same
  // auth_token cookie, so the 401 refresh below still applies to them
  baseUrl?: string;
}

const defaultHeaders: HeadersInit = {
  'Content-Type': 'application/json',
};

let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;


async function refreshToken(): Promise<void> {

  const response = await fetch(`${env.authBaseUrl}/refreshAuth`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Token refresh failed');
  }
}

function buildUrl(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>,
  baseUrl: string = env.apiBaseUrl
): string {
  const url = new URL(`${baseUrl}${endpoint}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const responseClone = response.clone();
    let errorData: unknown;

    try {
      errorData = await response.json();
    } catch {
      try {
        const text = await responseClone.text();
        errorData = text.length > 0 ? text : undefined;
      } catch {
        errorData = undefined;
      }
    }

    const error: ApiError = {
      status: response.status,
      statusText: response.statusText,
      message: `Request failed: ${response.statusText}`,
      data: errorData,
    };
    throw error;
  }
  
  
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

async function request<T>(
  method: string,
  endpoint: string,
  body?: unknown,
  options?: RequestOptions
): Promise<T> {
  const { params, headers, baseUrl, ...restOptions } = options ?? {};
  const url = buildUrl(endpoint, params, baseUrl);

  const makeRequest = () =>
    fetch(url, {
      credentials: 'include',
      method,
      headers: {
        ...defaultHeaders,
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      ...restOptions,
    });

  let response = await makeRequest();

  if (response.status === 401 && endpoint !== '/do/refreshAuth') {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshToken().finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
    }

    try {
      await refreshPromise;
      response = await makeRequest();
    } catch {
      //window.location.href = '/login';
      throw {
        status: 401,
        statusText: 'Unauthorized',
        message: 'Session expired',
      } as ApiError;
    }
  }

  return handleResponse<T>(response);
}

export const api = {
  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return request<T>('GET', endpoint, undefined, options);
  },

  post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('POST', endpoint, body, options);
  },

  put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PUT', endpoint, body, options);
  },

  patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PATCH', endpoint, body, options);
  },

  delete<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('DELETE', endpoint, body, options);
  },
};

export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'statusText' in error &&
    'message' in error
  );
}

// isAccessDeniedError is true for a 403, which the backend also uses for unknown companies.
export function isAccessDeniedError(error: unknown): boolean {
  return isApiError(error) && error.status === 403;
}


export { refreshToken }
